/**
 *
 * UpgradeCta
 *
 * The single "Upgrade…" affordance behind every plan-gated feature across the
 * dashboard (messaging, sharing, branding, terms, leads — the
 * ~13 call sites this replaces). All of them used to be a bare
 * `mailto:sales@movebooth.com` opened with `target="_blank"` — a blank,
 * orphaned tab on any device with no mail client configured — then briefly
 * the public marketing pricing page, which sells hardware, not a plan change,
 * and gets nobody to Stripe. Neither ever actually moved a subscriber onto a
 * new plan; this does.
 *
 * `isSubscriber` reuses the exact signal `ManageSubscriptionButton` already
 * uses to decide whether an account has a Stripe customer behind it
 * (`selectHasBillingPortal` — presence of `stripe_customer_id`, fail-open for
 * a backend that doesn't send the field yet). That is precisely the
 * distinction this CTA needs: a subscriber has a Stripe customer a portal
 * session can be minted for; a rental-only account (hardware/iPad rental,
 * no software subscription ever behind it) never went through Stripe and has
 * nothing for a portal session to manage.
 *
 * rails has sent `stripe_customer_id` on the session since rails #1224, so a
 * blank value is the normal way a rental account reaches the contact path.
 * The fail-open read only matters against an older backend that omits the
 * field: there every account starts out guessed "subscriber", and a rental
 * account that clicks through is caught by the portal endpoint itself 404ing
 * (rails' "no Stripe customer" response — see `useBillingPortalSession`),
 * which flips this to the contact path instead of a generic error toast.
 *
 * "No Stripe customer" is broader than "rental": rails answers the same way
 * for a comped or manually-invoiced account, and the session carries no
 * rental flag to tell them apart. The rental wording is the owner-confirmed
 * copy for that whole branch; the sales address stays visible either way.
 *
 * A container rather than a component, by this repo's own boundary (see
 * `ManageSubscriptionButton`): it reads Redux and mints a mutation, so it
 * belongs on this side of the line even though every call site treats it as a
 * prop-driven building block.
 *
 * `as` lets every call site keep its existing styled anchor (`ButtonLink` /
 * `UpgradeButtonLink`) so layout and visual weight are unchanged; only the
 * destination — and, for a subscriber, the loading/error behaviour of a real
 * network call where there used to be none — is new.
 */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { t } from 'copy/t';
import { useBillingPortalSession } from 'containers/App/api/mutations';
import { selectHasBillingPortal, selectUser } from 'containers/App/selectors';

const GENERIC_ERROR = t('toast.billing.genericError');

const SALES_EMAIL = 'sales@movebooth.com';
const RENTAL_MAILTO =
  `mailto:${SALES_EMAIL}` +
  `?subject=${encodeURIComponent('Movebooth Rental Upgrade')}` +
  `&body=${encodeURIComponent(
    "I'd like to upgrade my rental to a Movebooth subscription."
  )}`;

/*
 * RentalUpgradeCta
 *
 * The one branch of this CTA family with no self-serve backend today.
 * Isolated in its own small component so swapping it for a real checkout
 * later — the moment one exists — touches only this file, never the
 * subscriber path below.
 *
 * Deliberately a real mailto anchor, in the SAME tab (no `target="_blank"`):
 * a device with no mail client configured turns `target="_blank"` into an
 * orphaned blank tab with the CTA's whole job silently undone. The address is
 * also visible in the link text, for the same reason `PlanSelectionModal`'s
 * is — nothing here depends on a mail client actually opening.
 *
 * Exported so it can be exercised directly in tests without going through the
 * subscriber-detection branch in `UpgradeCta`.
 */
export const RentalUpgradeCta = ({ as: As, ...rest }) => (
  <As {...rest} href={RENTAL_MAILTO}>
    Upgrade your rental — contact us at {SALES_EMAIL}
  </As>
);

const UpgradeCta = ({ as: As, children, ...rest }) => {
  const isSubscriber = useSelector(selectHasBillingPortal());
  const user = useSelector(selectUser());
  const [isLoading, setIsLoading] = useState(false);
  // Set only when the PORTAL ENDPOINT ITSELF — the real authority per
  // `useBillingPortalSession` — has said this account has no Stripe customer
  // behind it. Only reachable when the session omitted `stripe_customer_id`
  // (an older backend), where `selectHasBillingPortal`'s fail-open read had
  // to guess "subscriber": the first click resolves that guess instead of
  // leaving the account at two failed calls and a generic error toast.
  const [noStripeCustomer, setNoStripeCustomer] = useState(false);
  const { mutateAsync } = useBillingPortalSession();

  if (!isSubscriber || noStripeCustomer) {
    return <RentalUpgradeCta as={As} {...rest} />;
  }

  // Whether the session actually told us this account has a Stripe customer,
  // as opposed to `isSubscriber` having fail-OPEN-assumed one because the
  // field is absent. Only in the absent case is a 404 from the portal
  // endpoint informative — an account with a server-CONFIRMED customer id
  // that still 404s is a genuine backend inconsistency, not evidence this is
  // a rental account, so it stays a plain error there.
  const customerIdUnknown = !('stripe_customer_id' in user);

  const openPortal = async () => {
    // A portal session is single-use and short-lived, so a second click
    // mid-flight would mint a second one and race the first one's redirect.
    if (isLoading) return;
    setIsLoading(true);
    try {
      let data;
      try {
        data = await mutateAsync({ flow: 'subscription_update' });
      } catch (flowError) {
        // The flow-scoped request failed — a genuine 422 `no_subscription`,
        // or rails not (yet, or ever) recognizing the param. Either way a
        // plain portal session is the right fallback rather than surfacing an
        // error over a param the customer never asked about.
        data = await mutateAsync({});
      }
      // A 2xx without a url is not success in any sense the customer cares
      // about, and navigating to `undefined` would take them to a 404 on our
      // own domain — worse than staying put with an explanation.
      if (!data || !data.url) {
        toast.error(GENERIC_ERROR);
        return;
      }
      window.location.href = data.url;
    } catch (error) {
      // `useBillingPortalSession`'s own doc: rails 404s `{ success: false }`
      // when the account has no Stripe customer at all. When the front end
      // never had a real signal either way, that is the backend resolving
      // the ambiguity — switch to the rental contact path rather than
      // reporting an "error" over what is actually a known, handled case.
      if (customerIdUnknown && error && error.statusCode === 404) {
        setNoStripeCustomer(true);
        return;
      }
      toast.error((error && error.error) || GENERIC_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <As {...rest} as="button" type="button" onClick={openPortal} disabled={isLoading}>
      {isLoading ? 'Opening…' : children}
    </As>
  );
};

export default UpgradeCta;
